import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase, getCurrentUserId } from '@/lib/supabase'
import { useAppContextStore } from './appContext'
import type { ChildPermissions } from '@/services/child-permissions.service'

export type UserType = 'parent' | 'child'
export type SubscriptionType = 'free' | 'paid'

const USER_CONTEXT_CACHE_TTL_MS = 60_000 // 1 minute
const USER_CONTEXT_STORAGE_KEY = 'harmonia_user_context'
const USER_CONTEXT_STORAGE_TTL_MS = 24 * 60 * 60 * 1000 // 24h for localStorage

export const useUserContextStore = defineStore('userContext', () => {
  // State
  const userType = ref<UserType | null>(null)
  const childPermissions = ref<ChildPermissions | null>(null)
  const subscriptionType = ref<SubscriptionType | null>(null)
  const loading = ref(false)

  // Short TTL cache to avoid repeated loadUserContext() across views
  let cacheUserId: string | null = null
  let cacheExpiry = 0
  // In-flight dedup: several components call loadUserContext() concurrently on
  // first mount (tab page + each per-kid slide), all before the TTL cache is
  // populated — Sentry flagged the resulting burst of identical
  // user_profiles?id=* requests as an N+1 (HARMONIA-3). Share one promise.
  let pendingLoad: Promise<void> | null = null

  // Computed
  const isChild = computed(() => userType.value === 'child')
  const isParent = computed(() => userType.value === 'parent')
  const canReviewOtherChildrenTasks = computed(() => 
    childPermissions.value?.can_review_other_children_tasks ?? false
  )
  const canNavigateBetweenChildren = computed(() => 
    isChild.value && canReviewOtherChildrenTasks.value
  )

  /**
   * Load user type and permissions. Uses short TTL cache and localStorage for instant UI.
   * Concurrent callers share a single in-flight promise so the underlying
   * user_profiles fetch runs at most once per burst (see HARMONIA-3).
   */
  async function loadUserContext(): Promise<void> {
    if (pendingLoad) return pendingLoad
    pendingLoad = _loadUserContext()
    try {
      await pendingLoad
    } finally {
      pendingLoad = null
    }
  }

  async function _loadUserContext(): Promise<void> {
    const now = Date.now()
    // Identité lue depuis la session LOCALE, pas via getUser().
    //
    // getUser() interroge GoTrue sur le réseau à chaque appel (il valide le JWT
    // côté serveur). Il était ici la toute première instruction, AVANT le test
    // de cache quelques lignes plus bas : chaque ré-entrée d'onglet payait donc
    // un aller-retour serveur pour finalement retourner du cache. Mesuré sur
    // device : 1 à 2 appels /auth/v1/user à CHAQUE bascule, présents dans tous
    // les relevés quelle que soit la destination.
    //
    // getSession() lit le stockage local (et ne rafraîchit que si le jeton a
    // expiré). Ici on a seulement besoin d'une identité pour choisir une clé de
    // cache côté client : la validation qui compte reste celle du serveur, via
    // les RLS, sur chaque requête de données. Aucune décision de sécurité ne
    // repose sur cette valeur.
    const { data: { session } } = await supabase.auth.getSession()
    const user = session?.user ?? null
    if (!user) {
      userType.value = null
      childPermissions.value = null
      subscriptionType.value = null
      cacheUserId = null
      try {
        localStorage.removeItem(USER_CONTEXT_STORAGE_KEY)
      } catch (_) { /* noop */ }
      return
    }
    if (cacheUserId === user.id && now < cacheExpiry) {
      return // use in-memory cached state
    }

    // Restore from localStorage for instant UI (guard/pages can read isChild without waiting)
    try {
      const stored = localStorage.getItem(USER_CONTEXT_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as { userId: string; userType: UserType; ts: number }
        if (parsed.userId === user.id && now < parsed.ts + USER_CONTEXT_STORAGE_TTL_MS) {
          userType.value = parsed.userType
          cacheUserId = user.id
          cacheExpiry = now + USER_CONTEXT_CACHE_TTL_MS
          if (parsed.userType === 'child') {
            await loadChildPermissions()
          } else {
            childPermissions.value = null
          }
          await loadSubscriptionType()
          loading.value = false
          return
        }
      }
    } catch (_) { /* noop */ }

    loading.value = true
    try {
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('user_type')
        .eq('id', user.id)
        .single()

      if (profileError) {
        console.error('Error loading user profile:', profileError)
        userType.value = 'parent'
        return
      }

      userType.value = ((profile as any).user_type as UserType) || 'parent'

      if (userType.value === 'child') {
        await loadChildPermissions()
      } else {
        childPermissions.value = null
      }

      await loadSubscriptionType()

      cacheUserId = user.id
      cacheExpiry = now + USER_CONTEXT_CACHE_TTL_MS
      try {
        localStorage.setItem(
          USER_CONTEXT_STORAGE_KEY,
          JSON.stringify({ userId: user.id, userType: userType.value, ts: now })
        )
      } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Error loading user context:', error)
    } finally {
      loading.value = false
    }
  }

  /**
   * Load child permissions
   */
  async function loadChildPermissions(): Promise<void> {
    if (!isChild.value) {
      childPermissions.value = null
      return
    }

    try {
      // Session locale : on ne veut qu'un id pour filtrer, pas une validation
      // serveur du jeton (cf. getCurrentUserId dans lib/supabase).
      const userId = await getCurrentUserId()
      if (!userId) return

      // Get child_id via children.auth_user_id
      const { data: child, error: childError } = await (supabase as any)
        .from('children')
        .select('id')
        .eq('auth_user_id', userId)
        .maybeSingle()

      if (childError || !child) {
        console.error('Error loading child:', childError)
        return
      }

      // Get permissions (minimal columns for hot path)
      const { data: permissions, error: permError } = await (supabase as any)
        .from('child_permissions')
        .select('id, child_id, can_mark_tasks_done, can_customize_avatar, can_view_rewards_catalog, can_redeem_rewards, can_see_other_children_progress, can_review_other_children_tasks, preset, created_at, updated_at')
        .eq('child_id', child.id)
        .single()

      if (permError) {
        console.error('Error loading child permissions:', permError)
        return
      }

      childPermissions.value = permissions as unknown as ChildPermissions
    } catch (error) {
      console.error('Error loading child permissions:', error)
    }
  }

  /**
   * Load subscription type from household
   */
  async function loadSubscriptionType(): Promise<void> {
    try {
      const appContextStore = useAppContextStore()
      const currentHousehold = appContextStore.currentHousehold

      if (!currentHousehold) {
        subscriptionType.value = null
        return
      }

      // Get subscription_type from household (column may not be in generated types)
      const { data: household, error } = await (supabase as any)
        .from('households')
        .select('subscription_type')
        .eq('id', currentHousehold.id)
        .single()

      if (error) {
        console.error('Error loading subscription type:', error)
        subscriptionType.value = 'free' // Default to free
        return
      }

      subscriptionType.value = ((household as any)?.subscription_type as SubscriptionType) || 'free'
    } catch (error) {
      console.error('Error loading subscription type:', error)
      subscriptionType.value = 'free' // Default to free
    }
  }

  /**
   * Get household subscription type (helper method)
   */
  function getHouseholdSubscriptionType(): SubscriptionType {
    return subscriptionType.value || 'free'
  }

  /**
   * Check if user can perform an action
   */
  function can(permission: keyof ChildPermissions): boolean {
    if (isParent.value) {
      return true // Parents can do everything
    }

    if (isChild.value && childPermissions.value) {
      return Boolean(childPermissions.value[permission] ?? false)
    }

    return false
  }

  /**
   * Reset context (on logout). Invalidates cache and localStorage.
   */
  function reset(): void {
    userType.value = null
    childPermissions.value = null
    subscriptionType.value = null
    cacheUserId = null
    cacheExpiry = 0
    try {
      localStorage.removeItem(USER_CONTEXT_STORAGE_KEY)
    } catch (_) { /* noop */ }
  }

  return {
    // State
    userType,
    childPermissions,
    subscriptionType,
    loading,

    // Computed
    isChild,
    isParent,
    canReviewOtherChildrenTasks,
    canNavigateBetweenChildren,

    // Methods
    loadUserContext,
    loadChildPermissions,
    loadSubscriptionType,
    getHouseholdSubscriptionType,
    can,
    reset
  }
})

